import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import posthog from "posthog-js";
import { diagnosticPostHog } from "./utils/posthogInit";
import { getPostHogStatus } from "./utils/posthogEvents";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import EmailVerification from "./pages/EmailVerification";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Layout from "./components/Layout";
import "./styles/App.css";
import "react-toastify/dist/ReactToastify.css";
import Customer from "./pages/customer/Customer";
import Transaction from "./pages/transaction/Transaction";
import Profile from "./pages/Profile/Profile";
import MultipleCyberRiskReport from "./pages/MultipleCyberRiskReport";
import ScanList from "./pages/ScanList/ScanList";
import SingleScanList from "./pages/SingleScan/SingleScanList";
import NotFound from "./pages/NotFound";
import CyberRiskReport from "./pages/cyberRiskReport/CyberRiskReport";
import MyDomainScan from "./pages/MyScan/MyDomainScan";
import DomainReport from "./pages/cyberRiskReport/DomainReport";

function App() {
  const { data: oemPartner } = useSelector((state) => state.oem);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const logo = oemPartner?.logo;

  // Diagnostic: Log PostHog status on mount
  React.useEffect(() => {
    console.log('🔍 App Mounted - PostHog Status:', getPostHogStatus());
    diagnosticPostHog();
  }, []);

  // Track page views with PostHog (only on allowed production environment)
  React.useEffect(() => {
    if (window.posthog && window.posthog.capture) {
      try {
        window.posthog.capture('page_view', {
          page_path: window.location.pathname,
          page_title: document.title,
          timestamp: new Date().toISOString(),
        });
        console.log('PostHog page_view event captured');
      } catch (error) {
        console.error('Failed to capture page_view:', error);
      }
    }
  }, [window.location.pathname]);

  // Track authentication changes (only on allowed production environment)
  React.useEffect(() => {
    if (window.posthog && window.posthog.capture) {
      try {
        if (isAuthenticated) {
          const userEmail = user?.email || user?.oemPartner?.email || user?.user?.email;
          const userId = user?.oemPartner?.id || user?.oemPartner?._id || user?._id || user?.id || userEmail;
          window.posthog.capture('user_authenticated', {
            email: userEmail,
            userId: userId,
            timestamp: new Date().toISOString(),
          });
          console.log('PostHog user_authenticated event captured', { email: userEmail });
        }
      } catch (error) {
        console.error('Failed to capture auth event:', error);
      }
    }
  }, [isAuthenticated, user]);

  // Set favicon based on authentication status
  const faviconUrl =
    isAuthenticated && logo
      ? logo
      : "https://washi.s3.ap-south-1.amazonaws.com/RR360%20with%20whitebg.png";
  // Navigation handlers
  const handleBackToLogin = () => {
    window.location.href = '/login';
  };

  return (
    <Router
    // basename={process.env.REACT_APP_BASE_PATH || "/oem"}
    >
      <Helmet>
        <link
          rel="icon"
          type="image/png"
          href={faviconUrl}
          sizes="16x16 32x32"
        />
        <link rel="shortcut icon" type="image/x-icon" href={faviconUrl} />
      </Helmet>

      <Routes>
        {/* Root redirects to CyberRiskReport */}
        <Route path="/" element={<Navigate to="/cyberRiskReport" replace />} />
        
        {/* Public CyberRiskReport Route */}
        <Route path="/cyberRiskReport" element={<CyberRiskReport />} />
        <Route path="/cyberRiskReport/verify-mail/:token/:email" element={<CyberRiskReport />} />
        <Route path="/domain-scan-result/:userId" element={<DomainReport />} />

        {/* Authentication Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/verify" element={<EmailVerification />} />
        <Route path="/verify" element={<EmailVerification />} />
        
        {/* Password Reset Routes */}
        <Route 
          path="/reset-password" 
          element={<ResetPassword onBackToLogin={handleBackToLogin} />} 
        />
        
        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="user" element={<Customer />} />
          <Route path="plan-transaction" element={<Transaction />} />
          <Route path="profile" element={<Profile />} />
          <Route path="single-scan" element={<SingleScanList />} />
          <Route path="scan-list" element={<ScanList />} />
          <Route path="scan" element={<MultipleCyberRiskReport />} />
          <Route path="my-domain-scan" element={<MyDomainScan />} />
          
        </Route>
        
        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ width: "320px" }}
        toastStyle={{ paddingRight: "40px" }}
      />
    </Router>
  );
}

export default App;
